import { Controller } from "@hotwired/stimulus"
import Rails from "@rails/ujs"

const STORAGE_KEY = "enrolment_draft"

const STEP_DATA = [
  { title: "Parent / Guardian Details", desc: "Please provide accurate contact and address information." },
  { title: "Student Information", desc: "Tell us about your child. You can add more than one." },
  { title: "Review & Agreement", desc: "Please review your details and accept the terms before submitting." }
]

export default class extends Controller {
  static targets = ["step1", "step2", "step3", "progressStep", "progressFill", "stepCount", "stepTitle", "stepDesc", "formErrors"]
  static values = {
    submitUrl: String,
    hasPolicies: Boolean
  }

  connect() {
    this.submitting = false
    this.currentStep = 1
    this.draft = this.getDraft()
    this.fillStep1FromDraft()
    this.fillStep2FromDraft()
    this.updateStudentTitles()
    this.updateStudentDividers()
    this.showStep(1)
    this.bindStep1()
    this.bindStep2()
    this.bindStep3()
  }

  getDraft() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  }

  setDraft(data) {
    this.draft = data
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }

  clearDraft() {
    this.draft = {}
    sessionStorage.removeItem(STORAGE_KEY)
  }

  stepPath(step) {
    return this.submitUrlValue.replace(/\/submit$/, `/step/${step}`)
  }

  showStep(step) {
    this.clearErrors()
    this.currentStep = step
    const totalSteps = this.hasStep3Target ? 3 : 2
    this.step1Target.classList.toggle("d-none", step !== 1)
    this.step2Target.classList.toggle("d-none", step !== 2)
    if (this.hasStep3Target) {
      this.step3Target.classList.toggle("d-none", step !== 3)
    }
    this.progressStepTargets.forEach((el, i) => {
      const n = i + 1
      el.classList.toggle("done", n < step)
      el.classList.toggle("active", n === step)
      const num = el.querySelector(".step-num")
      const check = el.querySelector(".fa-check")
      if (num) num.classList.toggle("d-none", n < step)
      if (check) check.classList.toggle("d-none", n >= step)
    })
    if (this.hasProgressFillTarget) {
      const pct = totalSteps > 1 ? ((step - 1) / totalSteps) * 100 : 0
      this.progressFillTarget.style.width = `${pct}%`
    }
    if (this.hasStepCountTarget) this.stepCountTarget.textContent = `Step ${step} of ${totalSteps}`
    if (this.hasStepTitleTarget && STEP_DATA[step - 1]) this.stepTitleTarget.textContent = STEP_DATA[step - 1].title
    if (this.hasStepDescTarget && STEP_DATA[step - 1]) this.stepDescTarget.textContent = STEP_DATA[step - 1].desc
    if (step === 2) {
      this.visibleStudentBlocks().forEach(b => this.prefillStudentAddress(b))
    }
    if (step === 3) this.renderSummary()
    history.replaceState({ step }, "", this.stepPath(step))

    const wizard = document.getElementById("enrolment-wizard")
    if (wizard) wizard.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  buildStep1Payload() {
    const a = document.getElementById("parent_first_name")?.value?.trim() || ""
    const b = document.getElementById("parent_last_name")?.value?.trim() || ""
    const customFieldValues = this.collectParentCustomFieldValues()
    return {
      parent_name: [a, b].filter(Boolean).join(" "),
      parent_email: document.getElementById("parent_email")?.value?.trim() || "",
      parent_mobile: document.getElementById("parent_mobile")?.value?.trim() || "",
      parent_address: document.getElementById("parent_address")?.value?.trim() || "",
      parent_city: document.getElementById("parent_city")?.value?.trim() || "",
      parent_postcode: document.getElementById("parent_postcode")?.value?.trim() || "",
      relationship: document.getElementById("parent_relationship")?.value?.trim() || "",
      occupation: document.getElementById("parent_occupation")?.value?.trim() || "",
      custom_field_values_attributes: customFieldValues
    }
  }

  collectParentCustomFieldValues() {
    const values = []
    const container = document.getElementById("parent-custom-fields")
    if (!container) return values

    const customFieldWrappers = container.querySelectorAll(".custom-field-wrapper")
    customFieldWrappers.forEach(wrapper => {
      const fieldId = wrapper.dataset.customFieldId
      if (!fieldId) return

      const input = wrapper.querySelector('input[type="radio"]:checked') ||
                    wrapper.querySelector('input[type="checkbox"]') ||
                    wrapper.querySelector("select") ||
                    wrapper.querySelector('input[type="text"]') ||
                    wrapper.querySelector('input[type="number"]') ||
                    wrapper.querySelector('input[type="date"]') ||
                    wrapper.querySelector('input[type="url"]')

      if (input) {
        let value = input.value
        if (input.type === "checkbox") {
          value = input.checked ? "true" : "false"
        }
        if (input.type === "radio" && !input.checked) {
          return // Skip unchecked radio buttons
        }
        values.push({
          custom_field_id: fieldId,
          value: value
        })
      }
    })
    return values
  }

  fillStep1FromDraft() {
    const d = this.draft.step1
    if (!d?.parent_name) return
    const names = d.parent_name.split(" ")
    const set = (id, v) => { const el = document.getElementById(id); if (el) el.value = v || "" }
    set("parent_first_name", names[0])
    set("parent_last_name", names.slice(1).join(" "))
    set("parent_email", d.parent_email)
    set("parent_mobile", d.parent_mobile)
    set("parent_address", d.parent_address)
    set("parent_city", d.parent_city)
    set("parent_postcode", d.parent_postcode)
    set("parent_relationship", d.relationship)
    set("parent_occupation", d.occupation)
  }

  // Returns all visible (not removed) student blocks
  visibleStudentBlocks() {
    return Array.from(document.querySelectorAll("#enrolment-students-container .student-block"))
      .filter(b => b.style.display !== "none")
  }

  collectStudents() {
    return this.visibleStudentBlocks().map(block => {
      const customFieldValues = this.collectCustomFieldValues(block)
      return {
        student_firstname: block.querySelector(".student-firstname")?.value?.trim() || "",
        student_lastname:  block.querySelector(".student-lastname")?.value?.trim() || "",
        dob:               block.querySelector(".student-dob")?.value?.trim() || "",
        gender:            block.querySelector(".student-gender")?.value?.trim() || "",
        student_address:   block.querySelector(".student-address")?.value?.trim() || "",
        student_postcode:  block.querySelector(".student-postcode")?.value?.trim() || "",
        ethnicity_group:   block.querySelector(".student-ethnicity")?.value?.trim() || "",
        sen:               block.querySelector('input[name*="[sen]"]:checked')?.value || "0",
        sen_notes:         block.querySelector(".student-sen-notes")?.value?.trim() || "",
        allergy:          block.querySelector('input[name*="[allergy]"]:checked')?.value || "0",
        allergy_notes:     block.querySelector(".student-allergy-notes")?.value?.trim() || "",
        custom_field_values_attributes: customFieldValues
      }
    })
  }

  collectCustomFieldValues(block) {
    const values = []
    const customFieldWrappers = block.querySelectorAll(".custom-field-wrapper")
    customFieldWrappers.forEach(wrapper => {
      const fieldId = wrapper.dataset.customFieldId
      if (!fieldId) return

      const input = wrapper.querySelector('input[type="radio"]:checked') ||
                    wrapper.querySelector('input[type="checkbox"]') ||
                    wrapper.querySelector("select") ||
                    wrapper.querySelector('input[type="text"]') ||
                    wrapper.querySelector('input[type="number"]') ||
                    wrapper.querySelector('input[type="date"]') ||
                    wrapper.querySelector('input[type="url"]')

      if (input) {
        let value = input.value
        if (input.type === "checkbox") {
          value = input.checked ? "true" : "false"
        }
        if (input.type === "radio" && !input.checked) {
          return // Skip unchecked radio buttons
        }
        values.push({
          custom_field_id: fieldId,
          value: value
        })
      }
    })
    return values
  }

  fillStudentBlock(block, s) {
    const set = (sel, val) => { const el = block.querySelector(sel); if (el) el.value = val || "" }
    set(".student-firstname", s.student_firstname)
    set(".student-lastname", s.student_lastname)
    set(".student-dob", s.dob)
    set(".student-gender", s.gender)
    set(".student-address", s.student_address)
    set(".student-postcode", s.student_postcode)
    set(".student-ethnicity", s.ethnicity_group)
    block.querySelectorAll('input[name*="[sen]"]').forEach(r => {
      r.checked = r.value === (s.sen || "0")
    })
  }

  fillStep2FromDraft() {
    const students = this.draft.step2?.students
    if (!students?.length) return

    const container = document.getElementById("enrolment-students-container")
    const addLink = document.getElementById("enrolment-add-student")
    if (!container) return

    // Fill the first pre-rendered block
    const firstBlock = container.querySelector(".student-block")
    if (firstBlock) this.fillStudentBlock(firstBlock, students[0])

    // For additional students, trigger link_to_add (synchronous jQuery click) then fill
    if (addLink) {
      for (let i = 1; i < students.length; i++) {
        $(addLink).trigger("click")
        const blocks = container.querySelectorAll(".student-block")
        this.fillStudentBlock(blocks[blocks.length - 1], students[i])
      }
    }
  }

  updateStudentTitles() {
    const visible = this.visibleStudentBlocks()
    visible.forEach((b, i) => {
      const title = b.querySelector(".student-title")
      if (title) title.textContent = `Student ${i + 1}`
      // Hide Remove link when only one student block is visible
      const removeLink = b.querySelector("a.remove_nested_fields")
      if (removeLink) removeLink.style.display = visible.length > 1 ? "" : "none"
    })
  }

  updateStudentDividers() {
    const visible = this.visibleStudentBlocks()
    visible.forEach((b, i) => {
      b.classList.toggle("border-top", i > 0)
      b.classList.toggle("pt-3", i > 0)
    })
  }

  renderSummary() {
    const el = document.getElementById("enrolment-summary")
    if (!el) return
    const step1 = this.draft.step1
    const students = this.draft.step2?.students || []
    const rows = [
      ["Parent / Guardian", step1?.parent_name || ""],
      ["Email", step1?.parent_email || ""]
    ]
    students.forEach((s, i) => {
      const name = [s.student_firstname, s.student_lastname].filter(Boolean).join(" ")
      rows.push([`Student ${i + 1}`, [name, s.dob, s.gender].filter(Boolean).join(" · ")])
    })
    el.innerHTML = `
      <div class="d-flex align-items-center mb-3">
        <span class="ew-divider-label">Application Summary</span>
        <hr class="flex-grow-1 ml-2 my-0">
      </div>
      <dl class="row border rounded p-3 mb-0 ew-student-card">
        ${rows.filter(r => r[1]).map(([k, v]) => `
          <dt class="col-5 text-uppercase fw-bold small text-muted">${k}</dt>
          <dd class="col-7 fw-bold mb-1">${v}</dd>
        `).join("")}
      </dl>`
  }

  notify(message) {
    if (!this.hasFormErrorsTarget) return
    this.formErrorsTarget.textContent = message
    this.formErrorsTarget.classList.remove("d-none")
  }

  clearErrors() {
    if (!this.hasFormErrorsTarget) return
    this.formErrorsTarget.textContent = ""
    this.formErrorsTarget.classList.add("d-none")
  }

  bindStep1() {
    const form = document.getElementById("enrolment-form-step1")
    if (!form) return
    form.addEventListener("submit", e => {
      e.preventDefault()
      const payload = this.buildStep1Payload()
      if (!payload.parent_name || !payload.parent_email || !payload.parent_mobile || !payload.parent_address || !payload.parent_city || !payload.parent_postcode || !payload.relationship) {
        this.notify("Please fill all required fields.")
        return
      }

      // Validate custom fields manually
      const customFieldsContainer = document.getElementById("parent-custom-fields")
      if (customFieldsContainer) {
        const wrappers = customFieldsContainer.querySelectorAll('.custom-field-wrapper');
        let hasError = false;

        wrappers.forEach(wrapper => {
          if (wrapper.classList.contains('d-none')) return;
          const isRequired = wrapper.dataset.fieldRequired === 'true';
          if (!isRequired) return;

          const input = wrapper.querySelector('.custom-field-input');
          if (!input) return;

          let hasValue = false;
          if (input.type === 'radio') {
            const checked = wrapper.querySelector('input[type="radio"]:checked');
            hasValue = !!checked;
          } else if (input.type === 'checkbox') {
            hasValue = input.checked;
          } else {
            hasValue = !!input.value.trim();
          }

          if (!hasValue) {
            hasError = true;
            wrapper.classList.add('is-invalid');
            if (!wrapper.querySelector('.custom-field-error')) {
              const errorDiv = document.createElement('div');
              errorDiv.className = 'custom-field-error text-danger small mt-1';
              errorDiv.textContent = 'This field is required';
              wrapper.appendChild(errorDiv);
            }
          }
        });

        if (hasError) {
          this.notify("Please fill all required custom fields.")
          return
        }
      }

      this.setDraft({ ...this.draft, step1: payload })
      this.showStep(2)
    })
  }

  bindStep2() {
    const form = document.getElementById("enrolment-form-step2")
    const backBtn = document.getElementById("enrolment-back-step2")
    if (backBtn) backBtn.addEventListener("click", e => { e.preventDefault(); this.showStep(1) })

    // Submit listener first — must be attached regardless of jQuery availability
    if (!form) return
    form.addEventListener("submit", e => {
      e.preventDefault()
      const students = this.collectStudents()
      if (!students.length || students.some(s => !s.student_firstname || !s.student_lastname || !s.dob || !s.gender)) {
        this.notify("Please add at least one student with all required fields.")
        return
      }
      this.setDraft({ ...this.draft, step2: { students } })
      if (!this.hasPoliciesValue) {
        this.doSubmit(students)
        return
      }
      this.showStep(3)
    })

    // jquery_nested_form fires these events when blocks are added/removed
    $(form).on("nested:fieldAdded", () => {
      const container = document.getElementById("enrolment-students-container")
      if (container) this.prefillStudentAddress(container.lastElementChild)
      this.updateStudentTitles()
      this.updateStudentDividers()
    })
    $(form).on("nested:fieldRemoved", () => {
      this.updateStudentTitles()
      this.updateStudentDividers()
    })
  }

  prefillStudentAddress(block) {
    const addr = block.querySelector(".student-address")
    const post = block.querySelector(".student-postcode")
    if (addr && !addr.value) addr.value = this.draft.step1?.parent_address || ""
    if (post && !post.value) post.value = this.draft.step1?.parent_postcode || ""
  }

  bindStep3() {
    const backBtn = document.getElementById("enrolment-back-step3")
    const form = document.getElementById("enrolment-form-step3")
    if (backBtn) backBtn.addEventListener("click", e => { e.preventDefault(); this.showStep(2) })
    if (!form) return
    form.addEventListener("submit", e => {
      e.preventDefault()
      this.doSubmit(this.draft.step2?.students || [])
    })
  }

  doSubmit(students) {
    if (this.submitting) return
    this.submitting = true
    const btn = document.getElementById("enrolment-submit-btn")
    if (btn) btn.disabled = true
    const enrolment_policy_acceptances_attributes = Array.from(
      document.querySelectorAll('input[name="policy_ids[]"]:checked')
    ).map(el => ({ client_policy_id: el.value })).filter(a => a.client_policy_id)

    const payload = {
      enrolment: {
        ...this.draft.step1,
        enrolment_items_attributes: students,
        enrolment_policy_acceptances_attributes
      }
    }
    fetch(this.submitUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": Rails.csrfToken(), Accept: "application/json" },
      body: JSON.stringify(payload)
    })
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.redirect_url) {
          this.clearDraft()
          window.location.href = data.redirect_url
        } else {
          this.submitting = false
          if (btn) btn.disabled = false
          const step = data.step || 1
          const errors = data.errors || ["Submission failed"]
          this.showStep(step)
          this.notify(errors.join(" "))
        }
      })
      .catch(() => {
        this.submitting = false
        if (btn) btn.disabled = false
        this.notify("Submission failed. Please try again.")
      })
  }
}
